import { useEffect } from 'react';

export type PageMetaOptions = {
  title: string;
  description: string;
  canonicalPath: string;
  robots?: string;
};

const SITE_ORIGIN = 'https://aboelo.de';
const SITE_BASE = `${SITE_ORIGIN}/fitness`;

const BASE_PATH = '/fitness';

/**
 * True only when the path already carries the base as a whole segment.
 * A plain startsWith('/fitness') would also match e.g. /fitnesskurse and
 * strip such a route of its base path.
 */
function hasBasePrefix(pathname: string): boolean {
  return pathname === BASE_PATH || pathname.startsWith(`${BASE_PATH}/`);
}

/**
 * Resolve a route path to the absolute URL that is actually served.
 *
 * Never emits a trailing slash — /fitness and /fitness/uebungen both answer
 * with 200, so the canonical has to name exactly one of them. The domain root
 * is the sole exception, where the slash belongs to the origin.
 * Query strings and fragments are dropped; a canonical names the bare document.
 */
function toAbsoluteUrl(path: string): string {
  const raw = path ?? '/';

  let absolute: string;
  if (/^https?:\/\//i.test(raw)) {
    absolute = raw;
  } else if (raw === '/' || raw === '') {
    absolute = SITE_BASE;
  } else if (hasBasePrefix(raw)) {
    absolute = `${SITE_ORIGIN}${raw}`;
  } else if (raw.startsWith('/')) {
    absolute = `${SITE_BASE}${raw}`;
  } else {
    absolute = `${SITE_BASE}/${raw}`;
  }

  absolute = absolute.split(/[?#]/)[0];

  if (/^https?:\/\/[^/]+\/?$/i.test(absolute)) {
    return absolute.endsWith('/') ? absolute : `${absolute}/`;
  }

  return absolute.replace(/\/+$/, '');
}

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

/**
 * Keep document title / description / canonical in sync for SPA SEO + prerender.
 */
export function usePageMeta({ title, description, canonicalPath, robots }: PageMetaOptions) {
  useEffect(() => {
    const canonical = toAbsoluteUrl(canonicalPath);

    document.title = title;
    document.documentElement.lang = 'de';

    upsertMeta('name', 'description', description);
    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:url', canonical);
    upsertMeta('property', 'og:type', 'website');

    if (robots) {
      upsertMeta('name', 'robots', robots);
    }

    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', canonical);
  }, [title, description, canonicalPath, robots]);
}

export { SITE_BASE, SITE_ORIGIN, toAbsoluteUrl };
